import { Locale } from "@/lib/i18n";

type FooterCopy = {
  home: string;
  allTools: string;
  guides: string;
  blog: string;
  about: string;
  contact: string;
  howItWorks: string;
  editorialPolicy: string;
  contactUs: string;
  email: string;
  subscribe: string;
  privacy: string;
  terms: string;
  sitemap: string;
  sourceCode: string;
  copyright: string;
};

type LayoutCopy = {
  toggleMenu: string;
  footer: FooterCopy;
};

const copy: Record<Locale, LayoutCopy> = {
  en: {
    toggleMenu: "Toggle menu",
    footer: {
      home: "Home",
      allTools: "All Tools",
      guides: "Guides",
      blog: "Blog",
      about: "About",
      contact: "Contact",
      howItWorks: "How It Works",
      editorialPolicy: "Editorial Policy",
      contactUs: "Contact us:",
      email: "Email",
      subscribe: "Subscribe to news",
      privacy: "Privacy Policy",
      terms: "Terms",
      sitemap: "Sitemap",
      sourceCode: "Source Code",
      copyright: "© 2025 dogufy.com. All Rights Reserved.",
    },
  },
  id: {
    toggleMenu: "Buka menu",
    footer: {
      home: "Beranda",
      allTools: "Semua Tools",
      guides: "Panduan",
      blog: "Blog",
      about: "Tentang",
      contact: "Kontak",
      howItWorks: "Cara Kerja",
      editorialPolicy: "Kebijakan Editorial",
      contactUs: "Hubungi kami:",
      email: "Email",
      subscribe: "Langganan kabar terbaru",
      privacy: "Kebijakan Privasi",
      terms: "Syarat",
      sitemap: "Peta Situs",
      sourceCode: "Kode Sumber",
      copyright: "© 2025 dogufy.com. Semua Hak Dilindungi.",
    },
  },
  vi: {
    toggleMenu: "Mở menu",
    footer: {
      home: "Trang chủ",
      allTools: "Tất cả công cụ",
      guides: "Hướng dẫn",
      blog: "Blog",
      about: "Giới thiệu",
      contact: "Liên hệ",
      howItWorks: "Cách hoạt động",
      editorialPolicy: "Chính sách biên tập",
      contactUs: "Liên hệ với chúng tôi:",
      email: "Email",
      subscribe: "Đăng ký nhận tin",
      privacy: "Chính sách quyền riêng tư",
      terms: "Điều khoản",
      sitemap: "Sơ đồ trang",
      sourceCode: "Mã nguồn",
      copyright: "© 2025 dogufy.com. Đã đăng ký bản quyền.",
    },
  },
  cs: {
    toggleMenu: "Otevřít menu",
    footer: {
      home: "Domů",
      allTools: "Všechny nástroje",
      guides: "Návody",
      blog: "Blog",
      about: "O nás",
      contact: "Kontakt",
      howItWorks: "Jak to funguje",
      editorialPolicy: "Redakční zásady",
      contactUs: "Kontaktujte nás:",
      email: "E-mail",
      subscribe: "Odebírat novinky",
      privacy: "Zásady ochrany osobních údajů",
      terms: "Podmínky",
      sitemap: "Mapa webu",
      sourceCode: "Zdrojový kód",
      copyright: "© 2025 dogufy.com. Všechna práva vyhrazena.",
    },
  },
  es: {
    toggleMenu: "Abrir menú",
    footer: {
      home: "Inicio",
      allTools: "Todas las herramientas",
      guides: "Guías",
      blog: "Blog",
      about: "Acerca de",
      contact: "Contacto",
      howItWorks: "Cómo funciona",
      editorialPolicy: "Política editorial",
      contactUs: "Contáctanos:",
      email: "Correo",
      subscribe: "Suscribirse a las novedades",
      privacy: "Política de privacidad",
      terms: "Términos",
      sitemap: "Mapa del sitio",
      sourceCode: "Código fuente",
      copyright: "© 2025 dogufy.com. Todos los derechos reservados.",
    },
  },
  fr: {
    toggleMenu: "Ouvrir le menu",
    footer: {
      home: "Accueil",
      allTools: "Tous les outils",
      guides: "Guides",
      blog: "Blog",
      about: "À propos",
      contact: "Contact",
      howItWorks: "Fonctionnement",
      editorialPolicy: "Politique éditoriale",
      contactUs: "Contactez-nous :",
      email: "E-mail",
      subscribe: "S'abonner aux actualités",
      privacy: "Politique de confidentialité",
      terms: "Conditions",
      sitemap: "Plan du site",
      sourceCode: "Code source",
      copyright: "© 2025 dogufy.com. Tous droits réservés.",
    },
  },
  de: {
    toggleMenu: "Menü öffnen",
    footer: {
      home: "Startseite",
      allTools: "Alle Tools",
      guides: "Anleitungen",
      blog: "Blog",
      about: "Über uns",
      contact: "Kontakt",
      howItWorks: "So funktioniert es",
      editorialPolicy: "Redaktionsrichtlinie",
      contactUs: "Kontaktieren Sie uns:",
      email: "E-Mail",
      subscribe: "Neuigkeiten abonnieren",
      privacy: "Datenschutzerklärung",
      terms: "Bedingungen",
      sitemap: "Sitemap",
      sourceCode: "Quellcode",
      copyright: "© 2025 dogufy.com. Alle Rechte vorbehalten.",
    },
  },
  it: {
    toggleMenu: "Apri menu",
    footer: {
      home: "Home",
      allTools: "Tutti gli strumenti",
      guides: "Guide",
      blog: "Blog",
      about: "Chi siamo",
      contact: "Contatti",
      howItWorks: "Come funziona",
      editorialPolicy: "Politica editoriale",
      contactUs: "Contattaci:",
      email: "Email",
      subscribe: "Iscriviti alle novità",
      privacy: "Informativa sulla privacy",
      terms: "Termini",
      sitemap: "Mappa del sito",
      sourceCode: "Codice sorgente",
      copyright: "© 2025 dogufy.com. Tutti i diritti riservati.",
    },
  },
  hu: {
    toggleMenu: "Menü megnyitása",
    footer: {
      home: "Kezdőlap",
      allTools: "Összes eszköz",
      guides: "Útmutatók",
      blog: "Blog",
      about: "Rólunk",
      contact: "Kapcsolat",
      howItWorks: "Hogyan működik",
      editorialPolicy: "Szerkesztési szabályzat",
      contactUs: "Kapcsolat:",
      email: "E-mail",
      subscribe: "Feliratkozás a hírekre",
      privacy: "Adatvédelmi szabályzat",
      terms: "Feltételek",
      sitemap: "Webhelytérkép",
      sourceCode: "Forráskód",
      copyright: "© 2025 dogufy.com. Minden jog fenntartva.",
    },
  },
  ms: {
    toggleMenu: "Buka menu",
    footer: {
      home: "Utama",
      allTools: "Semua alat",
      guides: "Panduan",
      blog: "Blog",
      about: "Tentang",
      contact: "Hubungi",
      howItWorks: "Cara ia berfungsi",
      editorialPolicy: "Dasar editorial",
      contactUs: "Hubungi kami:",
      email: "E-mel",
      subscribe: "Langgan berita",
      privacy: "Dasar privasi",
      terms: "Terma",
      sitemap: "Peta laman",
      sourceCode: "Kod sumber",
      copyright: "© 2025 dogufy.com. Hak cipta terpelihara.",
    },
  },
  nl: {
    toggleMenu: "Menu openen",
    footer: {
      home: "Home",
      allTools: "Alle tools",
      guides: "Gidsen",
      blog: "Blog",
      about: "Over",
      contact: "Contact",
      howItWorks: "Hoe het werkt",
      editorialPolicy: "Redactioneel beleid",
      contactUs: "Neem contact op:",
      email: "E-mail",
      subscribe: "Abonneer op nieuws",
      privacy: "Privacybeleid",
      terms: "Voorwaarden",
      sitemap: "Sitemap",
      sourceCode: "Broncode",
      copyright: "© 2025 dogufy.com. Alle rechten voorbehouden.",
    },
  },
  pl: {
    toggleMenu: "Otwórz menu",
    footer: {
      home: "Strona główna",
      allTools: "Wszystkie narzędzia",
      guides: "Poradniki",
      blog: "Blog",
      about: "O nas",
      contact: "Kontakt",
      howItWorks: "Jak to działa",
      editorialPolicy: "Polityka redakcyjna",
      contactUs: "Skontaktuj się z nami:",
      email: "E-mail",
      subscribe: "Subskrybuj aktualności",
      privacy: "Polityka prywatności",
      terms: "Warunki",
      sitemap: "Mapa witryny",
      sourceCode: "Kod źródłowy",
      copyright: "© 2025 dogufy.com. Wszelkie prawa zastrzeżone.",
    },
  },
  pt: {
    toggleMenu: "Abrir menu",
    footer: {
      home: "Início",
      allTools: "Todas as ferramentas",
      guides: "Guias",
      blog: "Blog",
      about: "Sobre",
      contact: "Contacto",
      howItWorks: "Como funciona",
      editorialPolicy: "Política editorial",
      contactUs: "Contacte-nos:",
      email: "E-mail",
      subscribe: "Subscrever novidades",
      privacy: "Política de privacidade",
      terms: "Termos",
      sitemap: "Mapa do site",
      sourceCode: "Código-fonte",
      copyright: "© 2025 dogufy.com. Todos os direitos reservados.",
    },
  },
  th: {
    toggleMenu: "เปิดเมนู",
    footer: {
      home: "หน้าแรก",
      allTools: "เครื่องมือทั้งหมด",
      guides: "ไกด์",
      blog: "บล็อก",
      about: "เกี่ยวกับ",
      contact: "ติดต่อ",
      howItWorks: "วิธีการทำงาน",
      editorialPolicy: "นโยบายบรรณาธิการ",
      contactUs: "ติดต่อเรา:",
      email: "อีเมล",
      subscribe: "สมัครรับข่าวสาร",
      privacy: "นโยบายความเป็นส่วนตัว",
      terms: "ข้อกำหนด",
      sitemap: "แผนผังเว็บไซต์",
      sourceCode: "ซอร์สโค้ด",
      copyright: "© 2025 dogufy.com. สงวนลิขสิทธิ์ทั้งหมด",
    },
  },
  tr: {
    toggleMenu: "Menüyü aç",
    footer: {
      home: "Ana sayfa",
      allTools: "Tüm araçlar",
      guides: "Rehberler",
      blog: "Blog",
      about: "Hakkında",
      contact: "İletişim",
      howItWorks: "Nasıl çalışır",
      editorialPolicy: "Editoryal politika",
      contactUs: "Bize ulaşın:",
      email: "E-posta",
      subscribe: "Haberler için abone ol",
      privacy: "Gizlilik politikası",
      terms: "Şartlar",
      sitemap: "Site haritası",
      sourceCode: "Kaynak kodu",
      copyright: "© 2025 dogufy.com. Tüm hakları saklıdır.",
    },
  },
  el: {
    toggleMenu: "Άνοιγμα μενού",
    footer: {
      home: "Αρχική",
      allTools: "Όλα τα εργαλεία",
      guides: "Οδηγοί",
      blog: "Blog",
      about: "Σχετικά",
      contact: "Επικοινωνία",
      howItWorks: "Πώς λειτουργεί",
      editorialPolicy: "Συντακτική πολιτική",
      contactUs: "Επικοινωνήστε μαζί μας:",
      email: "Email",
      subscribe: "Εγγραφή στα νέα",
      privacy: "Πολιτική απορρήτου",
      terms: "Όροι",
      sitemap: "Χάρτης ιστότοπου",
      sourceCode: "Πηγαίος κώδικας",
      copyright: "© 2025 dogufy.com. Με επιφύλαξη παντός δικαιώματος.",
    },
  },
  uk: {
    toggleMenu: "Відкрити меню",
    footer: {
      home: "Головна",
      allTools: "Усі інструменти",
      guides: "Гайди",
      blog: "Блог",
      about: "Про нас",
      contact: "Контакти",
      howItWorks: "Як це працює",
      editorialPolicy: "Редакційна політика",
      contactUs: "Зв'яжіться з нами:",
      email: "Ел. пошта",
      subscribe: "Підписатися на новини",
      privacy: "Політика конфіденційності",
      terms: "Умови",
      sitemap: "Мапа сайту",
      sourceCode: "Вихідний код",
      copyright: "© 2025 dogufy.com. Усі права захищено.",
    },
  },
  ru: {
    toggleMenu: "Открыть меню",
    footer: {
      home: "Главная",
      allTools: "Все инструменты",
      guides: "Гайды",
      blog: "Блог",
      about: "О нас",
      contact: "Контакты",
      howItWorks: "Как это работает",
      editorialPolicy: "Редакционная политика",
      contactUs: "Свяжитесь с нами:",
      email: "Эл. почта",
      subscribe: "Подписаться на новости",
      privacy: "Политика конфиденциальности",
      terms: "Условия",
      sitemap: "Карта сайта",
      sourceCode: "Исходный код",
      copyright: "© 2025 dogufy.com. Все права защищены.",
    },
  },
  ar: {
    toggleMenu: "فتح القائمة",
    footer: {
      home: "الرئيسية",
      allTools: "كل الأدوات",
      guides: "الأدلة",
      blog: "المدونة",
      about: "حول",
      contact: "اتصل بنا",
      howItWorks: "كيف يعمل",
      editorialPolicy: "السياسة التحريرية",
      contactUs: "تواصل معنا:",
      email: "البريد الإلكتروني",
      subscribe: "اشترك في الأخبار",
      privacy: "سياسة الخصوصية",
      terms: "الشروط",
      sitemap: "خريطة الموقع",
      sourceCode: "الكود المصدري",
      copyright: "© 2025 dogufy.com. جميع الحقوق محفوظة.",
    },
  },
  ko: {
    toggleMenu: "메뉴 열기",
    footer: {
      home: "홈",
      allTools: "모든 도구",
      guides: "가이드",
      blog: "블로그",
      about: "소개",
      contact: "문의",
      howItWorks: "작동 방식",
      editorialPolicy: "편집 정책",
      contactUs: "문의하기:",
      email: "이메일",
      subscribe: "새 소식 구독",
      privacy: "개인정보 처리방침",
      terms: "이용 약관",
      sitemap: "사이트맵",
      sourceCode: "소스 코드",
      copyright: "© 2025 dogufy.com. 모든 권리 보유.",
    },
  },
  ja: {
    toggleMenu: "メニューを開く",
    footer: {
      home: "ホーム",
      allTools: "すべてのツール",
      guides: "ガイド",
      blog: "ブログ",
      about: "概要",
      contact: "お問い合わせ",
      howItWorks: "仕組み",
      editorialPolicy: "編集方針",
      contactUs: "お問い合わせ:",
      email: "メール",
      subscribe: "ニュースを購読",
      privacy: "プライバシーポリシー",
      terms: "利用規約",
      sitemap: "サイトマップ",
      sourceCode: "ソースコード",
      copyright: "© 2025 dogufy.com. All Rights Reserved.",
    },
  },
  ro: {
    toggleMenu: "Deschide meniul",
    footer: {
      home: "Acasă",
      allTools: "Toate uneltele",
      guides: "Ghiduri",
      blog: "Blog",
      about: "Despre",
      contact: "Contact",
      howItWorks: "Cum funcționează",
      editorialPolicy: "Politica editorială",
      contactUs: "Contactează-ne:",
      email: "Email",
      subscribe: "Abonează-te la noutăți",
      privacy: "Politica de confidențialitate",
      terms: "Termeni",
      sitemap: "Hartă site",
      sourceCode: "Cod sursă",
      copyright: "© 2025 dogufy.com. Toate drepturile rezervate.",
    },
  },
};

export function getLayoutCopy(locale: Locale): LayoutCopy {
  return copy[locale] ?? copy.en;
}
